import { useState, useEffect } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useSynonyms, type Difficulty, type GameMode, type SynonymEntry } from './useSynonyms'
import { useSessionMode } from '../../professional/SessionModeContext'
import GameSetupScreen from '../GameSetupScreen'
import GameCompleteScreen from '../GameCompleteScreen'
import GamePlayHeader from '../GamePlayHeader'
import SaveErrorBanner from '../SaveErrorBanner'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useMobile } from '../../hooks/useMobile'
import { useTimer } from '../useTimer'
import { formatGameTime } from '../utils'
import { ss } from '../gameSetupStyles'
import { COLORS, SHADOWS, RADIUS, FONT } from '../../styles/tokens'
import { activities } from '../../api/activities'

type GameScreen = 'setup' | 'playing' | 'complete'

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']
const MODES: GameMode[] = ['synonyms', 'antonyms', 'mixed']

/* ── Root ──────────────────────────────────────────────────────── */
export default function SynonymsGame({ onBack }: { onBack: () => void }) {
  const { t, language } = useTranslation()
  const sessionMode = useSessionMode()
  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : 'setup')
  const [mode,       setMode]       = useState<GameMode>(
    (sessionMode?.config.mode as GameMode) ?? 'synonyms',
  )
  const [difficulty, setDifficulty] = useState<Difficulty>(
    (sessionMode?.config.difficulty as Difficulty) ?? 'easy',
  )
  const [entries,    setEntries]    = useState<SynonymEntry[]>([])
  const [loading,    setLoading]    = useState(true)
  const [loadError,  setLoadError]  = useState<string | null>(null)
  const isMobile = useMobile()

  const lang = language as 'es' | 'en'

  useEffect(() => {
    activities.get('synonyms')
      .then(detail => {
        const raw = (detail.data as { entries?: SynonymEntry[] } | null)
        setEntries(raw?.entries ?? [])
      })
      .catch(() => setLoadError(t.synonyms.loadError))
      .finally(() => setLoading(false))
  }, [t.synonyms.loadError])

  const hook = useSynonyms(entries, difficulty, mode, lang)
  const { gameComplete, moves, startTime, endTime } = hook.state

  const { sessionId } = useGameSession({
    activitySlug: 'synonyms',
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug:  'synonyms',
    difficulty,
    mode,
    moves,
    startTime,
    endTime,
    pairsTotal:   hook.state.questions.length,
    pairsMatched: hook.state.answered.size,
    sessionId,
    onCompleted:  () => setScreen('complete'),
  })

  const handleStart = () => {
    hook.resetGame()
    setScreen('playing')
  }

  if (loading) {
    return (
      <div style={{ ...s.loadRoot, ...(isMobile ? s.loadRootMobile : {}) }}>
        <div style={s.loadSpinner}>⏳</div>
        <p style={s.loadText}>{t.synonyms.loading}</p>
      </div>
    )
  }

  if (loadError) {
    return (
      <div style={{ ...s.loadRoot, ...(isMobile ? s.loadRootMobile : {}) }}>
        <p style={s.loadText}>{loadError}</p>
        <button style={s.loadBackBtn} onClick={onBack}>{t.synonyms.backToDashboard}</button>
      </div>
    )
  }

  return (
    <div style={s.root}>
      {saveError && (
        <SaveErrorBanner
          message={t.common.saveError}
          dismissLabel={t.common.dismissError}
          onDismiss={clearSaveError}
        />
      )}
      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          difficulty={difficulty}
          onModeChange={setMode}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
        />
      )}
      {screen === 'playing' && (
        <PlayScreen
          state={hook.state}
          difficulty={difficulty}
          lang={lang}
          onSelectEasyOption={hook.selectEasyOption}
          onSelectPoolWord={hook.selectPoolWord}
          onSkip={hook.skipQuestion}
          onToggleHardWord={hook.toggleHardWord}
          onBack={() => setScreen('setup')}
        />
      )}
      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="📖"
          title={t.synonyms.complete.title}
          subtitle={t.synonyms.complete.subtitle}
          stats={[
            { value: String(hook.state.moves),                                  label: t.synonyms.complete.movesLabel },
            { value: formatGameTime(hook.state.startTime, hook.state.endTime),  label: t.synonyms.complete.timeLabel },
            { value: t.synonyms.difficulty[difficulty].label,                   label: t.synonyms.complete.levelLabel },
          ]}
          playAgainLabel={t.synonyms.complete.playAgain}
          changeLevelLabel={t.synonyms.complete.changeLevel}
          backLabel={t.synonyms.complete.backToDashboard}
          onPlayAgain={() => { hook.resetGame(); setScreen('playing') }}
          onChangeLevel={() => setScreen('setup')}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ──────────────────────────────────────────────── */
function SetupScreen({ mode, difficulty, onModeChange, onDifficultyChange, onStart, onBack }: {
  mode:               GameMode
  difficulty:         Difficulty
  onModeChange:       (m: GameMode) => void
  onDifficultyChange: (d: Difficulty) => void
  onStart:            () => void
  onBack:             () => void
}) {
  const { t } = useTranslation()
  const sy = t.synonyms

  const modeIcons: Record<GameMode, string> = { synonyms: '🔤', antonyms: '↔️', mixed: '🔀' }

  const preview = (
    <div style={s.preview}>
      <div style={s.previewPrompt}>{mode === 'antonyms' ? sy.questionPromptAntonym : sy.questionPrompt}</div>
      <div style={s.previewWord}>grande</div>
      <div style={s.previewOptions}>
        {['enorme', 'pequeño', 'rápido', 'brillante'].map((w, i) => (
          <div key={w} style={{ ...s.previewOption, ...(i === 0 ? s.previewOptionCorrect : {}) }}>{w}</div>
        ))}
      </div>
    </div>
  )

  return (
    <GameSetupScreen
      title={sy.title}
      subtitle={sy.subtitle}
      backLabel={sy.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map(d => ({ id: d, label: sy.difficulty[d].label, hint: sy.difficulty[d].hint }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={id => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={sy.chooseDifficultyTitle}
      preview={preview}
      startLabel={sy.startBtn}
      onStart={onStart}
    >
      {/* Mode selector */}
      <div>
        <h2 style={ss.optionLabel}>{sy.chooseModeTitle}</h2>
        <div style={ss.modeGrid}>
          {MODES.map(m => (
            <button
              key={m}
              style={{ ...ss.modeCard, ...(m === mode ? ss.modeCardActive : {}) }}
              onClick={() => onModeChange(m)}
            >
              <span style={{ fontSize: FONT.size.xl }}>{modeIcons[m]}</span>
              <span style={{ fontSize: FONT.size.md, fontWeight: 'bold', color: COLORS.textPrimary }}>{sy.mode[m].label}</span>
              <span style={{ fontSize: FONT.size.sm, color: COLORS.textSecondary }}>{sy.mode[m].description}</span>
            </button>
          ))}
        </div>
      </div>
    </GameSetupScreen>
  )
}

/* ── Play Screen ───────────────────────────────────────────────── */
function PlayScreen({ state, difficulty, lang, onSelectEasyOption, onSelectPoolWord, onSkip, onToggleHardWord, onBack }: {
  state:                ReturnType<typeof useSynonyms>['state']
  difficulty:           Difficulty
  lang:                 'es' | 'en'
  onSelectEasyOption:   (w: string) => void
  onSelectPoolWord:     (w: string) => void
  onSkip:               () => void
  onToggleHardWord:     (w: string) => void
  onBack:               () => void
}) {
  const { t } = useTranslation()
  const isMobile = useMobile()
  const sy = t.synonyms

  const timer = useTimer(state.startTime, state.endTime)

  const progress = difficulty === 'medium'
    ? (state.answered.size / Math.max(1, state.questions.length)) * 100
    : (state.currentIdx / Math.max(1, state.questions.length)) * 100

  const currentQIdx = difficulty === 'medium' ? state.questionQueue[0] : state.currentIdx
  const currentQ    = state.questions[currentQIdx]

  if (!currentQ) return null

  const questionWord = currentQ.entry[lang].word
  const prompt       = currentQ.questionType === 'synonym' ? sy.questionPrompt : sy.questionPromptAntonym

  const answered     = difficulty === 'medium' ? state.answered.size : state.currentIdx
  const counter      = sy.questionCounter
    .replace('{n}', String(answered + 1))
    .replace('{total}', String(state.questions.length))

  return (
    <div style={{ ...s.playRoot, ...(isMobile ? s.playRootMobile : {}) }}>
      <GamePlayHeader
        title={sy.title}
        stats={[
          { value: String(state.moves), label: sy.moves },
          { value: timer,               label: sy.time  },
        ]}
        progress={progress}
        progressNow={answered}
        progressMax={state.questions.length}
        onBack={onBack}
        backLabel={sy.backToDashboard}
      />

      <div style={s.playContent}>
        <div style={s.counterText}>{counter}</div>
        <div style={s.promptText}>{prompt}</div>
        <div style={s.wordText}>{questionWord}</div>

        {difficulty === 'easy' && (
          <EasyOptions
            options={currentQ.options}
            correctFlashWord={state.correctFlashWord}
            wrongFlashWord={state.wrongFlashWord}
            onSelect={onSelectEasyOption}
          />
        )}

        {difficulty === 'medium' && (
          <MediumGame
            state={state}
            onSelectPoolWord={onSelectPoolWord}
            onSkip={onSkip}
            skipLabel={sy.skipBtn}
          />
        )}

        {difficulty === 'hard' && (
          <HardOptions
            options={currentQ.options}
            correctWords={currentQ.correctWords}
            selectedWords={state.selectedWords}
            allCorrectFlash={state.allCorrectFlash}
            wrongFlashWord={state.wrongFlashWord}
            onToggle={onToggleHardWord}
            instruction={sy.selectAllCorrect}
          />
        )}
      </div>
    </div>
  )
}

/* ── Easy Options ──────────────────────────────────────────────── */
function EasyOptions({ options, correctFlashWord, wrongFlashWord, onSelect }: {
  options:          string[]
  correctFlashWord: string | null
  wrongFlashWord:   string | null
  onSelect:         (w: string) => void
}) {
  return (
    <div style={s.optionsGrid}>
      {options.map(word => {
        const isCorrectFlash = word === correctFlashWord
        const isWrongFlash   = word === wrongFlashWord
        return (
          <button
            key={word}
            style={{
              ...s.optionBtn,
              ...(isCorrectFlash ? s.optionCorrect : {}),
              ...(isWrongFlash   ? s.optionWrong   : {}),
            }}
            className={isWrongFlash ? 'wm-shake' : undefined}
            onClick={() => onSelect(word)}
            disabled={correctFlashWord !== null}
          >
            {word}
          </button>
        )
      })}
    </div>
  )
}

/* ── Medium Pool ───────────────────────────────────────────────── */
function MediumGame({ state, onSelectPoolWord, onSkip, skipLabel }: {
  state:            ReturnType<typeof useSynonyms>['state']
  onSelectPoolWord: (w: string) => void
  onSkip:           () => void
  skipLabel:        string
}) {
  const isMobile = useMobile()
  return (
    <div style={s.mediumContainer}>
      <div style={{ ...s.poolGrid, ...(isMobile ? s.poolGridMobile : {}) }}>
        {state.pool.map(word => {
          const isWrong = word === state.wrongFlashWord
          return (
            <button
              key={word}
              style={{ ...s.poolBtn, ...(isWrong ? s.optionWrong : {}) }}
              className={isWrong ? 'wm-shake' : undefined}
              onClick={() => onSelectPoolWord(word)}
            >
              {word}
            </button>
          )
        })}
      </div>
      {state.questionQueue.length > 1 && (
        <button style={s.skipBtn} onClick={onSkip}>{skipLabel}</button>
      )}
    </div>
  )
}

/* ── Hard Multi-select ─────────────────────────────────────────── */
function HardOptions({ options, correctWords, selectedWords, allCorrectFlash, wrongFlashWord, onToggle, instruction }: {
  options:          string[]
  correctWords:     string[]
  selectedWords:    Set<string>
  allCorrectFlash:  boolean
  wrongFlashWord:   string | null
  onToggle:         (w: string) => void
  instruction:      string
}) {
  return (
    <div>
      <div style={s.hardInstruction}>{instruction}</div>
      <div style={s.hardGrid}>
        {options.map(word => {
          const isSelected   = selectedWords.has(word)
          const isCorrect    = correctWords.includes(word)
          const isWrongFlash = word === wrongFlashWord
          const isAllFlash   = allCorrectFlash && isCorrect
          return (
            <button
              key={word}
              style={{
                ...s.hardBtn,
                ...(isSelected && !isWrongFlash && !isAllFlash ? s.hardBtnSelected : {}),
                ...(isAllFlash   ? s.optionCorrect : {}),
                ...(isWrongFlash ? s.optionWrong   : {}),
              }}
              className={isWrongFlash ? 'wm-shake' : undefined}
              onClick={() => onToggle(word)}
              disabled={allCorrectFlash}
              aria-pressed={isSelected}
            >
              {word}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ── Styles ────────────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100dvh',
    background: COLORS.bgPage,
  },
  loadRoot: {
    minHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    padding: '2rem',
    background: COLORS.bgPage,
  },
  loadRootMobile: {
    padding: '1.5rem',
  },
  loadSpinner: {
    fontSize: '3rem',
  },
  loadText: {
    fontSize: FONT.size.md,
    color: COLORS.textBody,
    textAlign: 'center',
  },
  loadBackBtn: {
    padding: '0.75rem 1.5rem',
    background: COLORS.greenDark,
    color: '#fff',
    border: 'none',
    borderRadius: RADIUS.md,
    fontSize: FONT.size.md,
    cursor: 'pointer',
    minHeight: '48px',
  },
  // Preview in setup
  preview: {
    background: COLORS.bgCard,
    borderRadius: RADIUS.lg,
    padding: '1.25rem',
    boxShadow: SHADOWS.card,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    alignItems: 'center',
  },
  previewPrompt: {
    fontSize: FONT.size.base,
    color: COLORS.textMuted,
    fontStyle: 'italic',
  },
  previewWord: {
    fontSize: FONT.size.h2,
    fontWeight: 'bold',
    color: COLORS.greenDark,
  },
  previewOptions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.5rem',
    width: '100%',
  },
  previewOption: {
    padding: '0.5rem 0.75rem',
    background: COLORS.bgPage,
    border: `1.5px solid ${COLORS.borderWarm}`,
    borderRadius: RADIUS.md,
    fontSize: FONT.size.base,
    textAlign: 'center',
    color: COLORS.textBody,
  },
  previewOptionCorrect: {
    background: '#E8F5E9',
    borderColor: COLORS.greenDark,
    color: COLORS.greenDark,
    fontWeight: 'bold',
  },
  // Play
  playRoot: {
    minHeight: '100dvh',
    background: COLORS.bgPage,
    display: 'flex',
    flexDirection: 'column',
  },
  playRootMobile: {},
  playContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '1.25rem',
    gap: '1rem',
    maxWidth: '640px',
    margin: '0 auto',
    width: '100%',
  },
  counterText: {
    fontSize: FONT.size.base,
    color: COLORS.textMuted,
    alignSelf: 'flex-start',
  },
  promptText: {
    fontSize: FONT.size.xl,
    color: COLORS.textMuted,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  wordText: {
    fontSize: FONT.size.h2,
    fontWeight: 'bold',
    color: COLORS.greenDark,
    textAlign: 'center',
    letterSpacing: '-0.5px',
  },
  // Easy options
  optionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
    width: '100%',
  },
  optionBtn: {
    padding: '1rem 0.75rem',
    background: COLORS.bgCard,
    border: `2px solid ${COLORS.borderWarm}`,
    borderRadius: RADIUS.md,
    fontSize: FONT.size.md,
    color: COLORS.textBody,
    cursor: 'pointer',
    minHeight: '64px',
    fontWeight: '500',
    boxShadow: SHADOWS.card,
    transition: 'background 0.15s, border-color 0.15s',
    textAlign: 'center',
  },
  optionCorrect: {
    background: '#E8F5E9',
    borderColor: COLORS.greenDark,
    color: COLORS.greenDark,
    fontWeight: 'bold',
  },
  optionWrong: {
    background: '#FFEBEE',
    borderColor: '#E53935',
    color: '#C62828',
  },
  // Medium pool
  mediumContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
  },
  poolGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '0.625rem',
    width: '100%',
  },
  poolGridMobile: {},
  poolBtn: {
    padding: '0.875rem 0.625rem',
    background: COLORS.bgCard,
    border: `2px solid ${COLORS.borderWarm}`,
    borderRadius: RADIUS.md,
    fontSize: FONT.size.md,
    color: COLORS.textBody,
    cursor: 'pointer',
    minHeight: '56px',
    fontWeight: '500',
    boxShadow: SHADOWS.card,
    textAlign: 'center',
  },
  skipBtn: {
    padding: '0.75rem 2rem',
    background: 'transparent',
    border: `1.5px solid ${COLORS.borderWarm}`,
    borderRadius: RADIUS.md,
    fontSize: FONT.size.base,
    color: COLORS.textMuted,
    cursor: 'pointer',
    minHeight: '48px',
  },
  // Hard options
  hardInstruction: {
    fontSize: FONT.size.base,
    color: COLORS.textMuted,
    fontStyle: 'italic',
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  hardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '0.625rem',
    width: '100%',
  },
  hardBtn: {
    padding: '0.875rem 0.625rem',
    background: COLORS.bgCard,
    border: `2px solid ${COLORS.borderWarm}`,
    borderRadius: RADIUS.md,
    fontSize: FONT.size.md,
    color: COLORS.textBody,
    cursor: 'pointer',
    minHeight: '56px',
    fontWeight: '500',
    boxShadow: SHADOWS.card,
    textAlign: 'center',
    transition: 'background 0.15s, border-color 0.15s',
  },
  hardBtnSelected: {
    background: '#E8F0FE',
    borderColor: '#3949AB',
    color: '#1A237E',
    fontWeight: 'bold',
  },
}
